/**
 * @fileoverview Classification & Control Marking levels per Astro
 * (Rocket Communications) — colors mirror SF 706–712 + SF-902 hex values
 * and text colors are chosen for contrast per the Astro spec. The values
 * are NOT required by the government but match what government users
 * recognize on workstation labels, physical labels, and other apps.
 *
 * CJIS context: Criminal Justice Information is CUI-classified by NARA's
 * Controlled Unclassified Information Registry. IronCase ships defaulted
 * to `cui`. Surfaces handling cleared classified data (none today) would
 * override on a per-environment basis.
 */

export type ClassificationLevel =
  | "unclassified"
  | "cui"
  | "confidential"
  | "secret"
  | "topsecret"
  | "topsecretsci";

export interface ClassificationLevelDef {
  /** Lowercase machine slug. */
  key: ClassificationLevel;
  /** All-caps banner text per Astro spec (level fully spelled out, except CUI). */
  bannerText: string;
  /** Abbreviated portion-marking text per Astro spec. */
  portionText: string;
  /** Background hex from SF 706–712 / SF-902. */
  background: string;
  /** Foreground — `"white"` or `"black"` per Astro contrast guidance. */
  foreground: "white" | "black";
}

export const CLASSIFICATION_LEVELS: Readonly<Record<ClassificationLevel, ClassificationLevelDef>> = {
  unclassified: {
    key: "unclassified",
    bannerText: "UNCLASSIFIED",
    portionText: "(U)",
    background: "#007a33",
    foreground: "white",
  },
  cui: {
    key: "cui",
    // Per Astro Dec-2024 guidance: must read "CUI", not "CONTROLLED" or
    // "CONTROLLED UNCLASSIFIED INFORMATION".
    bannerText: "CUI",
    portionText: "(CUI)",
    background: "#502b85",
    foreground: "white",
  },
  confidential: {
    key: "confidential",
    bannerText: "CONFIDENTIAL",
    portionText: "(C)",
    background: "#0033a0",
    foreground: "white",
  },
  secret: {
    key: "secret",
    bannerText: "SECRET",
    portionText: "(S)",
    background: "#c8102e",
    foreground: "white",
  },
  topsecret: {
    key: "topsecret",
    bannerText: "TOP SECRET",
    portionText: "(TS)",
    background: "#ff8c00",
    foreground: "black",
  },
  topsecretsci: {
    key: "topsecretsci",
    bannerText: "TOP SECRET//SCI",
    portionText: "(TS//SCI)",
    background: "#fce83a",
    foreground: "black",
  },
};
